import { useEffect, useMemo, useState } from 'react';

import { useUserSourcesWithSearch } from '@/hooks/rxdb-models';

import type { RxDocument } from 'rxdb';
import type { UserSource } from '@flintmint/core-shared/rxdb';
import type { SearchResult } from '@/services/search-service/types';

export type NoteUserSourceMap = Record<string, { index?: number; enabled?: boolean }>;

export const getSourceId = (item: SourceSearchItem) => getSourceDocument(item).id;
export const isSearchResult = (item: SourceSearchItem): item is SearchResult => 'document' in item;
export const getSourceDocument = (item: SourceSearchItem) => (isSearchResult(item) ? item.document : item);

export type SourceSearchItem = SearchResult | UserSource;

export const toDeleteCandidate = (item: SourceSearchItem) => {
  const sourceDoc = getSourceDocument(item);
  const normalized = (sourceDoc as RxDocument<UserSource>).toJSON?.() ?? sourceDoc;
  return { ...normalized, type: 'userSources' as const };
};

export const useSourceListModel = ({
  ids = [],
  showAllUserSources = false,
  noteUserSources = {},
  resetSearchWhenNoAttachedState = false,
  resetSearchKey,
}: UseSourceListModelParams) => {
  const [searchTerm, setSearchTerm] = useState('');

  const { data: searchedUserSources = [], isLoading } = useUserSourcesWithSearch({
    ids: showAllUserSources ? undefined : ids,
    sort: [{ createdAt: 'desc' }],
    availabilityScopes: showAllUserSources ? ['global'] : undefined,
    searchTerm,
  });

  const sortedUserSources = useMemo(() => {
    if (showAllUserSources) {
      return searchedUserSources;
    }

    return [...searchedUserSources].sort((a, b) => {
      const indexA = noteUserSources[getSourceId(a)]?.index ?? Number.MAX_SAFE_INTEGER;
      const indexB = noteUserSources[getSourceId(b)]?.index ?? Number.MAX_SAFE_INTEGER;

      return indexB - indexA;
    });
  }, [searchedUserSources, noteUserSources, showAllUserSources]);

  useEffect(() => {
    if (resetSearchWhenNoAttachedState && searchTerm) {
      setSearchTerm('');
    }
  }, [resetSearchWhenNoAttachedState, searchTerm]);

  useEffect(() => {
    if (searchTerm) {
      setSearchTerm('');
    }
    // deps: reset only when resetSearchKey changes, not on every keystroke
  }, [resetSearchKey]);

  return {
    searchTerm,
    setSearchTerm,
    isLoading,
    sortedUserSources,
  };
};

interface UseSourceListModelParams {
  ids?: string[];
  showAllUserSources?: boolean;
  noteUserSources?: NoteUserSourceMap;
  resetSearchWhenNoAttachedState?: boolean;
  resetSearchKey?: number;
}
